import { apiFetch, withAuth } from "../common/api";

const HOST = import.meta.env.VITE_API_HOST;

/**
 * Tries to find customer by his id
 * @param {object} accessToken
 * @param {string} id
 * @returns {Promise<RegisteredCustomer | 404>}
 */
export async function findCustomerById(accessToken, id) {
  let resp;
  try {
    const opts = { method: "GET" };
    resp = await apiFetch(
      `${HOST}/api/v1/customer/by-id/${id}`,
      accessToken ? withAuth(accessToken, opts) : opts,
    );
  } catch {
    throw "Failed to call API";
  }

  console.log(resp);
  if (resp.status === 404) {
    throw "Пользователь не найден";
  }

  let customer;
  try {
    customer = await resp.json();
  } catch {
    throw `Failed to find by id with ${resp.text}`;
  }

  return customer;
}

/**
 * Tries to find customer by his phone number
 * @param {object} accessToken
 * @param {string} phoneNumber
 * @returns {Promise<Customer | 404>}
 */
export async function findByPhoneNumber(accessToken, phoneNumber) {
  let resp;
  try {
    resp = await apiFetch(
      `${HOST}/api/v1/customer/by-phone/${phoneNumber}`,
      withAuth(accessToken, { method: "GET" }),
    );
  } catch {
    throw "Failed to call API";
  }

  console.log(resp);
  if (resp.status === 404) {
    throw "Пользователь не найден";
  }

  let customer;
  try {
    customer = await resp.json();
  } catch {
    throw `Failed to find by phone number with ${resp.text}`;
  }

  return customer;
}

/**
 * Tries to find customer by his QR code
 * @param {object} accessToken
 * @param {string} qrCode
 * @returns {Promise<Customer>}
 */
export async function findByQrCode(accessToken, qrCode) {
  let resp;
  try {
    resp = await apiFetch(
      `${HOST}/api/v1/customer/by-qrcode/${qrCode}`,
      withAuth(accessToken, { method: "GET" }),
    );
  } catch (e) {
    console.error("Failed to call api with", JSON.stringify(e));
    throw "Failed to call API";
  }

  console.log(resp);
  if (resp.status === 404) {
    throw "Пользователь не найден";
  }

  let customer;
  try {
    customer = await resp.json();
  } catch {
    throw `Failed to find by Qr code with ${resp.text}`;
  }

  return customer;
}

/**
 * Tries to buy minutes for the customer
 * @param {object} accessToken
 * @param {BuyMinutesRequest} req
 * @returns {Promise<BuyMinutesResponse>}
 */
export async function buyMinutes(accessToken, req) {
  console.debug("Buying minutes for:", JSON.stringify(req));

  const resp = await apiFetch(
    `${HOST}/api/v1/customer/buy-minutes`,
    withAuth(accessToken, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }),
  );
  if (resp.status == 400) {
    throw { text: "Необходимо полностью погасить задолженность" };
  }

  if (resp.status != 200) {
    throw await resp.json();
  }

  const data = await resp.json();
  console.debug("Buy minutes response:", data);
  return data;
}

/**
 * @param {object} accessToken
 * @param {number} customerId
 * @returns {Promise<Transaction[]>}
 */
export async function fetchTransactions(accessToken, customerId) {
  console.log("Fetching transactions");
  const resp = await apiFetch(
    `${HOST}/api/v1/customer/${customerId}/transactions`,
    withAuth(accessToken),
  );
  console.log("Got", resp.status, "for the fetched transactions");
  const data = await resp.json();
  return data;
}

/**
 * @param {object} accessToken
 * @param {CheckoutRequest} req
 * @returns {Promise<CheckoutResponse>}
 */
export async function checkoutMinutes(accessToken, req) {
  console.log("Checking out minutes");
  const resp = await apiFetch(
    `${HOST}/api/v1/customer/checkout`,
    withAuth(accessToken, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }),
  );
  const data = await resp.json();
  return data;
}

/**
 * @param {CheckoutResponse} req
 * @returns {req is SuccessCheckoutResponse}
 */
export function isCheckoutMinutesSucceed(req) {
  return !("debt" in req && req.debt > 0);
}

/**
 * @param {object} accessToken
 * @param {ImportPaperWalletRequest} req
 */
export async function importPaperWallet(accessToken, req) {
  const resp = await apiFetch(
    `${HOST}/api/v1/customer/import-paper-wallet`,
    withAuth(accessToken, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }),
  );

  if (resp.status != 200) {
    throw "Failed to import paper wallet";
  }
}

/**
 * @param {object} accessToken
 * @param {number} page
 * @returns {Promise<{items: Array<RegisteredCustomer>, hasNext: boolean, page: number}>}
 */
export async function fetchAll(accessToken, page = 1) {
  const url = new URL(`${HOST}/api/v1/customers`);
  url.searchParams.set("page", String(page));
  const resp = await apiFetch(
    url.toString(),
    accessToken ? withAuth(accessToken) : undefined,
  );
  return await resp.json();
}

/**
 * @param {object} accessToken
 * @param {UpdateCustomerRequest} req
 */
export async function update(accessToken, req) {
  const opts = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  };
  const resp = await apiFetch(
    `${HOST}/api/v1/customers`,
    accessToken ? withAuth(accessToken, opts) : opts,
  );

  if (resp.status != 200) {
    throw await resp.text();
  }
}

/**
 * @param {object} accessToken
 * @param {SearchRequest} req
 * @param {number} [page=1]
 * @returns {Promise<{items: Array, hasNext: boolean, page: number}>}
 * @throws {string}
 */
export async function search(accessToken, req, page = 1) {
  const url = new URL(`${HOST}/api/v1/customers/search`);
  url.searchParams.set("page", String(page));
  const opts = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  };
  const resp = await apiFetch(
    url.toString(),
    accessToken ? withAuth(accessToken, opts) : opts,
  );

  if (resp.status != 200) {
    throw await resp.text();
  }

  const data = await resp.json();
  return data;
}

/**
 @param {object} accessToken
 @param {string} orderId
 */
export async function cancelOnlineOrder(accessToken, orderId) {
  const resp = await apiFetch(
    `${HOST}/api/v1/customer/online-order/${orderId}`,
    withAuth(accessToken, { method: "DELETE" }),
  );
  if (resp.status != 200) {
    throw await resp.text();
  }
}

/**
 * @param {object} accessToken
 * @returns {string}
 */
export async function initUpdatesSubscription(accessToken) {
  const resp = await apiFetch(
    `${HOST}/api/v1/customers/updates`,
    withAuth(accessToken),
  );
  if (resp.status != 200) {
    throw await resp.text();
  }
  return await resp.json();
}

/**
 * @param {string} eventSourceId
 * @returns {EventSource}
 */
export async function subscribeToUpdates(eventSourceId) {
  return new EventSource(`${HOST}/api/v1/customers/updates/${eventSourceId}`);
}

/**
 * @param {string} eventSourceId
 * @param {Array<number>} customerIds
 * @returns {void}
 */
export async function subscribeToCustomerUpdates(
  accessToken,
  eventSourceId,
  customerIds,
) {
  console.debug("Subscribing to the customer ids:", customerIds.join(", "));
  const resp = await apiFetch(
    `${HOST}/api/v1/customers/updates/${eventSourceId}`,
    withAuth(accessToken, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ customerIds }),
    }),
  );
  if (resp.status != 201) {
    throw await resp.text();
  }
}

/**
 * @param {string} accessToken
 * @param {string} eventSourceId
 * @param {number} customerId
 * @returns {Promise<void>}
 */
export async function unsubscribeFromCustomer(
  accessToken,
  eventSourceId,
  customerId,
) {
  console.debug("Unsubscribing from customer:", customerId);
  const resp = await apiFetch(
    `${HOST}/api/v1/customers/updates/${eventSourceId}/${customerId}`,
    withAuth(accessToken, {
      method: "DELETE",
    }),
  );
  if (resp.status != 204) {
    throw await resp.text();
  }
}

/**
 * @param {object} accessToken
 * @param {number} customerId
 * @returns {Promise<void>}
 */
export async function deleteCustomer(accessToken, customerId) {
  const resp = await apiFetch(
    `${HOST}/api/v1/customers/${customerId}`,
    withAuth(accessToken, { method: "DELETE" }),
  );
  if (resp.status != 200) {
    throw await resp.text();
  }
}

/**
 * @param {object} accessToken
 * @param {number} transactionId
 * @param {string} reason
 * @returns {Promise<{message: string}>}
 */
export async function cancelTransaction(
  accessToken,
  transactionId,
  reason = "Cancelled by admin",
) {
  const url = new URL(`${HOST}/api/v1/customer/transactions/${transactionId}`);
  url.searchParams.set("reason", reason);

  const resp = await apiFetch(
    url.toString(),
    withAuth(accessToken, { method: "DELETE" }),
  );
  if (resp.status != 200) {
    throw await resp.text();
  }
  return await resp.json();
}

/**
 * Restore customer access by phone number
 * @param {object} accessToken
 * @param {string} phone
 * @param {boolean} enableEmailNotifications
 * @returns {Promise<RegisteredCustomerWithTransactions>}
 */
export async function restoreAccess(
  accessToken,
  phone,
  enableEmailNotifications = false,
) {
  const resp = await apiFetch(
    `${HOST}/api/v1/customer/restore-access`,
    withAuth(accessToken, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        phone,
        enable_email_notifications: enableEmailNotifications,
      }),
    }),
  );

  if (resp.status === 404) {
    throw "Пользователь не найден";
  }

  if (resp.status !== 200) {
    throw await resp.text();
  }

  return await resp.json();
}
